/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';
import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';


/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Static class that provides utility methods and properties for functions
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzFunction extends EzStaticClass {
    /**
     * @static
     * @public @method
     * Empty function for use as a placeholder as needed
     *
     * @param {any} ...args
     *
     * @returns {any}
     * Returns the arguments passed to the method (if any)
     */
    static DUMMY_HANDLER(...args) {
        /* left empty on purpose */

        return args;
    }

    /**
     * @static
     * @public @method
     * Returns a function based upon the following logic:
     * 1) If the provided value is a valid function, returns the provided value
     * 2) If the provided value is undefined, null, or not a function, then returns EzFunction.DUMMY_HANDLER function
     *
     * @param {undefined|null|any|function} aFunction
     *
     * @returns {function}
     */
    static functionOrDummy(aFunction) {
        return EzFunction.isFunction(aFunction)
            ? aFunction
            : EzFunction.DUMMY_HANDLER;
    }

    /**
     * @static
     * @public @method
     * Gets true if the passed ref is a javascript function
     *
     * @param {undefined|null|function|*} aFunction
     * @returns {boolean}
     */
    static isFunction(value) {
        return undefined !== value && null !== value &&
            ('function' === typeof value || '[object Function]' === EzObject.getObjectType(value));
    }

    /**
     * @static
     * @public @method
     * If the provided value is a function, then the value is returned.
     * Otherwise, null is returned.
     *
     * @param {undefined|null|function|*} value
     * @returns {null|function}
     */
    static functionOrNull(value) {
        return EzFunction.isFunction(value)
            ? value
            : null;
    }

    /**
     * @static
     * @public @method
     * If the provided value is a function, then the value is returned.
     * Otherwise, the provided defaultValue is returned.
     * @param {undefined|null|function|*} value
     * @param {undefined|null|function|*} defaultValue
     * Default: EzFunction.dummyFunction
     *
     * @returns {undefined|null|function|*}
     */
    static functionOrDefault(value, defaultValue = EzFunction.DUMMY_HANDLER) {
        return EzFunction.isFunction(value)
            ? value
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Verifies the passed function is valid, and returns the call to that method. Otherwise, returns null
     * The first argument is expected to be the function to call back (if a valid function)
     * The remaining arguments get passed to the function when called.
     *
     * @param {function} functionToCallBack
     * @param {any} ...args
     *
     * @returns {any}
     * Returns the result of calling the function.
     */
    static callback(...args) {
        if (EzNumber.ZERO === args?.length || !EzFunction.isFunction(args[EzNumber.FIRST_INDEX])) {
            // Nothing to call
            return;
        }

        const callbackFunction = args[EzNumber.FIRST_INDEX];

        const callBackFunctionArguments = args.slice(EzNumber.ONE);

        return callbackFunction.apply(this, callBackFunctionArguments);
    }

    /**
     * @static
     * @public @method
     * Calls the provided functionToCall passing along the arrayOfParams provided (if any).
     * If the optionalThisContext is valid, will call the function with the provided context.
     * Otherwise, the current this context is used.
     * @returns {null|undefined|*}
     * Returns the result of calling the provided functionToCall
     */
    static callWithParams(functionToCall, arrayOfParams, optionalThisContext) {
        if (!EzFunction.isFunction(functionToCall)) {
            throw new EzBadParamException(
                'functionToCall',
                EzFunction,
                EzFunction.callWithParams);
        }
        if (EzObject.isValid(arrayOfParams) && !EzArray.isArray(arrayOfParams)) {
            throw new EzBadParamException(
                'arrayOfParams',
                EzFunction,
                EzFunction.callWithParams);
        }

        return functionToCall.apply(
            EzObject.assignOrDefault(
                optionalThisContext,
                this),
                EzArray.arrayOrNull(arrayOfParams));
    }

    /**
     * @static
     * @public @method
     * Place holder function that performs no actions.
     *
     * @param {any} ..args
     *
     * @returns {any}
     * Returns all the parameters passed to the function (if any)
     *
     * @deprecated
     * Migrate to: EzFunction.DUMMY_HANDLER(args)
     */
    static dummyFunction(...args) {
        return EzFunction.DUMMY_HANDLER(args);
    }
}
